import axios from 'axios';
import API_CONFIG from '../utils/config';
import { getToken, removeToken, removeUserData } from '../utils/storage';
import cacheService from './cacheService';
import networkService from './networkService';

// Axios-Instanz erstellen
const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request-Interceptor: Token automatisch hinzufügen
api.interceptors.request.use(
  async (config) => {
    const token = await getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response-Interceptor: Bei 401/403 automatisch ausloggen, bei Netzwerkfehler Cache nutzen
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      // Token ungültig oder Zugriff verweigert - ausloggen
      await removeToken();
      await removeUserData();
      // Hier könnte man auch zur Login-Seite navigieren
      // Das wird über den AuthContext gesteuert
    }
    
    // Bei Netzwerkfehler: Markiere als offline
    if (error.message === 'Network Error' || !error.response) {
      console.log('📴 Network error detected, app is offline');
      error.isOffline = true;
    }
    
    return Promise.reject(error);
  }
);

/**
 * Wrapper für GET-Requests mit automatischem Caching
 */
export const cachedGet = async (url, cacheKey, options = {}) => {
  const { maxAge, forceRefresh = false } = options;
  
  // Wenn forceRefresh, immer vom Server laden (auch wenn offline wird versucht)
  if (forceRefresh || networkService.getIsOnline()) {
    try {
      const response = await api.get(url);
      // Cache die Antwort für Offline-Nutzung
      if (cacheKey) {
        await cacheService.set(cacheKey, response.data);
      }
      return { data: response.data, fromCache: false };
    } catch (error) {
      // Bei Fehler: Versuche aus Cache zu laden (nur wenn nicht forceRefresh)
      if (!forceRefresh) {
        console.log('⚠️ Request failed, trying cache...');
        if (cacheKey) {
          const cached = await cacheService.get(cacheKey, maxAge);
          if (cached) {
            console.log('✅ Returning cached data after error');
            return { data: cached, fromCache: true };
          }
        }
      }
      throw error;
    }
  }
  
  // Offline und kein forceRefresh: Lade aus Cache
  if (cacheKey) {
    const cached = await cacheService.get(cacheKey, maxAge);
    if (cached) {
      console.log('📴 Offline - Returning cached data');
      return { data: cached, fromCache: true };
    }
  }
  
  throw new Error('No cached data available and offline');
};

export default api;

// Helper-Funktionen für häufige Fehlerbehandlung
export const handleApiError = (error) => {
  if (error.response) {
    // Server hat mit Fehlercode geantwortet
    return error.response.data?.error || 'Ein Fehler ist aufgetreten';
  } else if (error.request) {
    // Request wurde gesendet, aber keine Antwort erhalten
    return 'Keine Verbindung zum Server';
  } else {
    // Fehler beim Setup des Requests
    return error.message || 'Ein unbekannter Fehler ist aufgetreten';
  }
};

export { api };

